import { IEKGEvent } from '../store/ekgImport.types'
import { IItem } from '../../timeline/store/timeline.types'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { TimelineConstants } from '../../timeline/store/timeline.constants'
import { IVentilatorState } from '../../procedures/ventilator/store/ventilator.types'
import { generateClientCorrelationId } from '../../nemsisData/utils/customElements.util'
import { INemsisSnomedLookup } from '../../../components/procedure/store/procedure.types'
import { getUpdatedProcedureStateWithNemsisCode, handleNemsisCodeError } from '../../../utilities/nemsisCode.util'

interface CreateVentilatorProcedureProps {
  procedureEvent: IEKGEvent
  ptaCutoff: Date | null
  nemsisCodeOptions: INemsisSnomedLookup[]
}

export interface VentilatorProcedureObject {
  timelineItem: IItem
  ventilatorState: IVentilatorState
}

const createVentilatorProcedure = (props: CreateVentilatorProcedureProps): VentilatorProcedureObject | null => {
  const { procedureEvent, ptaCutoff, nemsisCodeOptions } = props

  if (!procedureEvent.procedureAction) return null

  const clientEventId = (procedureEvent.eventVitals.clientEventId as number).toString()
  const vsAction = procedureEvent.eventVitals.vsAction
  const isEkgImport = procedureEvent.eventVitals.isEkgImport

  const itemComments = procedureEvent.procedureComments || ''
  const itemSubHeader = `By , Action: Initiate, FiO2: , Air Temperature: ° C, Comments: ${itemComments}.`

  const eventDate = new Date(procedureEvent.dtDevice)
  const isPTA = !!ptaCutoff && eventDate < ptaCutoff

  const clientCorrelationId = generateClientCorrelationId(
    nemsisCodeOptions,
    TimelineConstants.VENTILATOR_PROCEDURE,
    null,
    {
      filter1: null,
      filter2: null,
      filter3: null,
    },
    clientEventId
  )

  const timelineItem: IItem = {
    key: clientEventId,
    clientCorrelationId,
    itemType: 'Procedures',
    event: 'Ventilator',
    grid: true,
    labelHeaderFlag: isPTA ? 'PTA' : '',
    labelHeader: procedureEvent.eventVitals.time,
    subHeader: procedureEvent.eventVitals.date,
    eventSubHeader: itemSubHeader,
    rowActionType: TimelineConstants.SET_VENTILATOR_PROCEDURE_ITEM,
    defaultHref: TabConstants.VENTILATOR_PROCEDURE,
    date: new Date(procedureEvent.dtDevice),
    clientEventId: clientEventId,
    ...(vsAction && { vsAction: vsAction }),
    ...(isEkgImport && { isEkgImport: isEkgImport }),
  }

  const ventilatorState: IVentilatorState = {
    key: clientEventId,
    ventilatorCheck: false,
    mode: null,
    breathType: null,
    settingsRespRate: '',
    tidalVolume: '',
    inspPressure: '',
    pEEP: '',
    fiO2: '',
    airTemperature: '',
    airTemperatureUnit: {
      id: 'c',
      name: 'C',
    },
    airTemperatureBool: false,
    humidityAir: '',
    iTime: '',
    pressureSupport: '',
    flowRate: '',
    sensitivity: '',
    meanAirwayPressure: '',
    deltaP: '',
    hertz: '',
    iNO: '',
    peakInspPressure: '',
    plateauPressure: '',
    autoPEEP: '',
    minuteVent: '',
    iERatio: '',
    measurementRespRate: '',
    exhaledTidalVolume: '',
    eTCuffPressureMeasurement: '',
    inspiratoryResistance: '',
    staticCompliance: '',
    expiratoryTimeConstant: '',
    assessedBy: null,
    procedureComplications: [],
    response: null,
    responsePNNV: null,
    authorization: null,
    comments: itemComments,
    addToTimeline: true,
    updateInputRows: false,
    date: procedureEvent.eventVitals.date,
    time: procedureEvent.eventVitals.time,
    successfulAttempt: true,
    pta: isPTA,
    attempts: '1',
    action: { id: 1, name: 'Initiate' },
    type: 'Ventilator',
    size: '',
    rolePerforming: null,
    rolePerformingPNNV: null,
    isEkgImport: 1,
    nemsisCode: null,
    procedurePerformedByAnotherUnit: null,
    procedurePerformedByAnotherUnitPNNV: null,
  }

  try {
    getUpdatedProcedureStateWithNemsisCode({
      procedureState: ventilatorState,
      actionField: null,
      filter1Field: null,
      filter2Field: null,
      filter3Field: null,
      nemsisCodeOptions,
    })
  } catch (error: unknown) {
    handleNemsisCodeError(error, ventilatorState)
  }

  const ventilatorProcedureObject: VentilatorProcedureObject = {
    ventilatorState,
    timelineItem,
  }

  return ventilatorProcedureObject
}

export default createVentilatorProcedure
